/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react';
import { Form, Button, Stack } from 'react-bootstrap';
import { getSetting, setSetting } from '../../data';
import { SettingsContext } from '../SettingsContext';
import { useTheme } from 'styled-components';

function NetworkSettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const [dnsServer, setDnsServer] = useState<string>('8.8.8.8');
    const [originalDnsServer, setOriginalDnsServer] = useState<string>('8.8.8.8');
    const [curlMaxTime, setCurlMaxTime] = useState<string>('30000');
    const [originalCurlMaxTime, setOriginalCurlMaxTime] = useState<string>('30000');
    const [curlConnectTimeout, setCurlConnectTimeout] = useState<string>('10000');
    const [originalCurlConnectTimeout, setOriginalCurlConnectTimeout] = useState<string>('10000');
    const [refresh, setRefresh] = useState<number>(0);
    const settingsContext = useContext(SettingsContext);

    const dirty = dnsServer !== originalDnsServer || curlMaxTime !== originalCurlMaxTime || curlConnectTimeout !== originalCurlConnectTimeout;

    useEffect(() => {
        settingsContext.setIsDirty(dirty);
    }, [dirty, settingsContext]);

    useEffect(() => {
        async function load() {
            try {
                const rcDnsServer = await getSetting('/dns/server');
                setDnsServer(rcDnsServer || '8.8.8.8');
                setOriginalDnsServer(rcDnsServer || '8.8.8.8');

                const rcCurlMaxTime = await getSetting('/curl/maxTime');
                setCurlMaxTime(rcCurlMaxTime || '30000');
                setOriginalCurlMaxTime(rcCurlMaxTime || '30000');

                const rcCurlConnectTimeout = await getSetting('/curl/connectTimeout');
                setCurlConnectTimeout(rcCurlConnectTimeout || '10000');
                setOriginalCurlConnectTimeout(rcCurlConnectTimeout || '10000');
            } catch (err: any) {
                console.error('Error loading network settings:', err);
            }
        }
        load();
    }, [refresh]);

    async function save() {
        await setSetting('/dns/server', dnsServer, 'DNS server');
        await setSetting('/curl/maxTime', curlMaxTime, 'Maximum time for curl request (ms)');
        await setSetting('/curl/connectTimeout', curlConnectTimeout, 'Timeout for curl connection (ms)');
        setRefresh(r => r + 1);
    }

    const changedStyle = (changed: boolean): React.CSSProperties => changed ? {
        borderLeft: `3px solid ${theme?.colors?.info || '#0dcaf0'}`,
        paddingLeft: '0.75rem'
    } : {};

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Network</h3>
                    <div className="text-muted">DNS and curl probe timeout configuration.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button variant="outline-secondary" onClick={() => {
                            setDnsServer(originalDnsServer);
                            setCurlMaxTime(originalCurlMaxTime);
                            setCurlConnectTimeout(originalCurlConnectTimeout);
                        }}>Clear</Button>
                        <Button variant="primary" onClick={save}>Save</Button>
                    </Stack>
                )}
            </div>

            <div className="mb-4" style={changedStyle(dnsServer !== originalDnsServer)}>
                <Form.Label htmlFor="dnsServer" className="fw-semibold">DNS Server</Form.Label>
                <div className="text-muted small mb-2">The DNS server to use for lookups.</div>
                <Form.Control
                    id="dnsServer"
                    name="dnsServer"
                    type="text"
                    value={dnsServer}
                    onChange={(e) => setDnsServer(e.target.value)}
                    style={{ maxWidth: '28rem' }}
                    placeholder="8.8.8.8"
                />
            </div>

            <div className="mb-4" style={changedStyle(curlMaxTime !== originalCurlMaxTime)}>
                <Form.Label htmlFor="curlMaxTime" className="fw-semibold">Curl Maximum Time</Form.Label>
                <div className="text-muted small mb-2">Total maximum time for a curl request to complete (milliseconds).</div>
                <Form.Control
                    id="curlMaxTime"
                    name="curlMaxTime"
                    type="number"
                    value={curlMaxTime}
                    onChange={(e) => setCurlMaxTime(e.target.value)}
                    style={{ maxWidth: '28rem' }}
                    placeholder="30000"
                />
            </div>

            <div className="mb-4" style={changedStyle(curlConnectTimeout !== originalCurlConnectTimeout)}>
                <Form.Label htmlFor="curlConnectTimeout" className="fw-semibold">Curl Connect Timeout</Form.Label>
                <div className="text-muted small mb-2">Maximum time to wait for a curl connection to establish (milliseconds).</div>
                <Form.Control
                    id="curlConnectTimeout"
                    name="curlConnectTimeout"
                    type="number"
                    value={curlConnectTimeout}
                    onChange={(e) => setCurlConnectTimeout(e.target.value)}
                    style={{ maxWidth: '28rem' }}
                    placeholder="10000"
                />
            </div>
        </div>
    );
}

export default NetworkSettings;
